import { FC } from "react";
import { Link } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";
import { companyInitials, companyName as companyNameOf } from "@1-now/booking-core";

/**
 * Brand mark.
 *
 * Renders the FMS-uploaded company logo when available. When `company_logo`
 * is empty, falls back to a color-matching badge: a gold tile with the
 * company initials plus a small wordmark. The fallback uses the active
 * theme's primary / text colors so it adapts to whatever palette the
 * tenant has configured (Design A or Design B).
 *
 * The uploaded picture is held inside a logoWidth x logoHeight box (120x60; 200x150 for company
 * 1592), scaled to fit and never stretched, so a tall or square upload cannot make the header or
 * the phone menu's top taller (1NOW-11181).
 *
 * `compact` is Design A's brand left of the header search (1NOW-11153), sized as Design B's
 * (1NOW-11192): the image 32px tall on phones and 40px from sm, its width capped (40px at 320,
 * 56px from 360, 112px from sm, 160px beside the pill at lg, 96px beside the full form at xl) so it never reaches the search;
 * the tile a little smaller, the name only between lg and xl, where the search is a pill.
 */
const Logo: FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { companyDetails, setSelectedWeeks, ourCompany } = useData();
  const time = moment();
  const timeInMilliseconds = time.valueOf();

  const companyLogo = companyDetails?.company_logo
    ? `${companyDetails?.company_logo}?date=${timeInMilliseconds}`
    : "";

  const companyId = ourCompany?.company?.id;
  const logoWidth = companyId === 1592 ? 200 : 120;
  const logoHeight = companyId === 1592 ? 150 : 60;

  const handleClick = () => {
    setSelectedWeeks([]);
  };

  // Resolve a display name from any of the places it might live.
  const companyName: string = companyNameOf(companyDetails, ourCompany);

  const initials = companyInitials(companyName);

  return (
    // No explicit text color on the wrapper — inherit from the parent so
    // the wordmark stays legible in any context (light header bg, dark
    // footer bg, etc.). When this was hardcoded to `text-text-color`, the
    // footer rendered navy-on-navy and the company name disappeared.
    <Link
      to="/"
      onClick={handleClick}
      className={`ttnc-logo group inline-flex items-center gap-3 focus:outline-none focus:ring-0 ${compact ? "shrink-0" : ""}`}
      aria-label={companyName || "Home"}
    >
      {companyLogo && compact ? (
        <img
          src={companyLogo}
          alt={companyName ? `${companyName} logo` : "Logo"}
          className="h-8 w-auto max-h-8 max-w-[2.5rem] object-contain min-[360px]:max-w-[3.5rem] sm:h-10 sm:max-h-10 sm:max-w-[7rem] lg:max-w-[10rem] xl:max-w-[6rem]"
        />
      ) : companyLogo ? (
        // The box is logoWidth x logoHeight at most and the picture is scaled to fit it, never
        // stretched: a wide logo still comes out logoWidth wide, but a tall or square one is held
        // to logoHeight instead of pushing the header (or the phone menu's top) several times
        // taller (1NOW-11181). It keeps its own proportions, so nothing is cropped.
        <img
          width={logoWidth}
          height={logoHeight}
          src={companyLogo}
          alt={companyName ? `${companyName} logo` : "Logo"}
          className="h-auto w-auto object-contain"
          style={{ maxWidth: logoWidth, maxHeight: logoHeight }}
        />
      ) : (
        <>
          {/* Initials badge — uses the theme's primary tones so the logo
              always matches the active color palette. */}
          <span className={`flex ${compact ? "h-9 w-9 shrink-0 sm:h-10 sm:w-10" : "h-11 w-11"} items-center justify-center rounded-2xl bg-gradient-to-br from-primary-brand to-primary-brand shadow-md shadow-primary-brand/30 ring-1 ring-primary-brand/40 transition-transform duration-300 group-hover:scale-105`}>
            {initials ? (
              <span className="text-base font-extrabold tracking-tight text-button-color">
                {initials}
              </span>
            ) : (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-button-color"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="2" />
                <path d="M12 2v4" />
                <path d="m4.93 4.93 2.83 2.83" />
                <path d="M2 12h4" />
              </svg>
            )}
          </span>
          {/* Company name only. This used to be a two-line lockup with a
              "Premium Rentals" subtitle under the name, hardcoded for every
              tenant on every design — untrue for the operators renting
              budget vans, moving trucks and economy cars. There is no field
              behind it: `api/website/details/` carries no brand-subtitle of
              any kind. The near misses are worse than nothing — `seo_title`
              is a full page title that already repeats the company name
              (median 34 chars, up to 64: "Water Source USA Inc – Affordable
              & Reliable Car Rental Services"), and `banner_tagline` is the
              hero's floating badge, so borrowing it double-prints the same
              words on the homepage. So the lockup degrades to the name
              alone rather than putting a claim in the operator's mouth.
              A real subtitle needs a dedicated FMS field first.

              Inherits color from the wrapper Link so the wordmark adapts to
              dark or light surroundings. */}
          {companyName && (
            <span
              className={`text-base font-extrabold leading-tight tracking-tight ${
                compact ? "hidden max-w-[9rem] truncate lg:block xl:hidden" : "hidden sm:block"
              }`}
            >
              {companyName}
            </span>
          )}
        </>
      )}
    </Link>
  );
};

export default Logo;
